import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import SortOutlinedIcon from '@mui/icons-material/SortOutlined';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import { useMemo, type ReactElement } from 'react';
import { CLONE_TABLE_PANEL_HEADER_HEIGHT } from '@/features/shared/components/tableSystem/tableConstants';
import { buildToolbarIconCircleSx } from '@/features/shared/components/tableSystem/tableToolbar.helpers';
import { SuiteExecutionsTable } from '@/features/suites/components/SuiteExecutionsTable';
import { SuitesTable } from '@/features/suites/components/SuitesTable';
import { gridQueryKey } from '@/shared/hooks/queryKeys';
import { isLightTheme } from '@/theme/semanticTokens';
import { r, s } from '@/theme/tokens';
import { Stack } from '@/ui';
import { buildLocalQueryResult } from '@/ui/composites/grid/localGridUtils';
import { Box, Cancel, Text, useTheme } from '@/ui/primitives';
import { Tab, Tabs } from '@/ui/primitives/Tabs';
import { landingCopy } from './landingCopy';
import {
  landingExampleDateLabel,
  landingSuiteMetricSeriesBySuiteId,
  landingTagColorByName,
  suiteExecutionsRows,
  suitesTableRows,
} from './landingData';
import { LANDING_BODY_FONT_SIZE } from './landingTypography';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { QueryResult, SuiteExecutionDto, SuiteListItemDto } from '@flowqa/shared';

type ExecutionTablesCarouselSectionProps = {
  width: number;
  carouselIndex: number;
  direction: 'next' | 'prev';
  onPrev: () => void;
  onNext: () => void;
  onSelect: (index: number) => void;
  slides: Array<{ id: string }>;
  suiteExecutionsQuery: (input: GridQueryInput) => Promise<QueryResult<SuiteExecutionDto>>;
  suitesQuery: (input: GridQueryInput) => Promise<QueryResult<SuiteListItemDto>>;
};

const LANDING_TABLE_PAGE_SIZE = 5;
const LANDING_TABLE_UI_FONT_SIZE = LANDING_BODY_FONT_SIZE;

const LandingToolbarPreview = (): ReactElement => {
  const theme = useTheme();
  const toolbarInactiveIconColor = isLightTheme(theme) ? 'text.secondary' : 'common.white';
  const iconSx = {
    color: toolbarInactiveIconColor,
    width: 32,
    height: 32,
    minWidth: 32,
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 0,
    pointerEvents: 'none',
    userSelect: 'none',
    '& .MuiSvgIcon-root': {
      display: 'block',
      fontSize: theme.app.iconSizes.md,
    },
  } as const;

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap="sm"
      sx={{
        minWidth: 0,
        height: `${CLONE_TABLE_PANEL_HEADER_HEIGHT}px`,
        minHeight: `${CLONE_TABLE_PANEL_HEADER_HEIGHT}px`,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          pr: s('sm'),
          pointerEvents: 'none',
          userSelect: 'none',
        }}
      >
        <Text
          role="bodyMuted"
          sx={{
            fontSize: LANDING_TABLE_UI_FONT_SIZE,
            lineHeight: (muiTheme) => muiTheme.typography.subtitle1.lineHeight,
          }}
        >
          {landingExampleDateLabel}
        </Text>
      </Box>
      {[EventOutlinedIcon, ViewColumnOutlinedIcon, FilterListOutlinedIcon, SortOutlinedIcon].map(
        (IconComponent, index) => (
          <Box key={`landing-toolbar-icon-${index}`} sx={iconSx}>
            <Box sx={buildToolbarIconCircleSx(false)}>
              <IconComponent />
            </Box>
          </Box>
        ),
      )}
      <Box sx={iconSx}>
        <Box sx={buildToolbarIconCircleSx(false)}>
          <SearchOutlinedIcon />
        </Box>
      </Box>
      <Box sx={iconSx}>
        <Box sx={buildToolbarIconCircleSx(false)}>
          <Cancel fontSize="small" />
        </Box>
      </Box>
    </Stack>
  );
};

export const ExecutionTablesCarouselSection = ({
  width,
  carouselIndex,
  onSelect,
  suiteExecutionsQuery,
  suitesQuery,
}: ExecutionTablesCarouselSectionProps): ReactElement => {
  const theme = useTheme();
  const tabValue = carouselIndex === 0 ? 'suites' : 'suite-executions';
  const initialSuitesData = useMemo(
    () =>
      buildLocalQueryResult(suitesTableRows, {
        page: 0,
        pageSize: LANDING_TABLE_PAGE_SIZE,
        sorts: [],
        filters: [],
      }),
    [],
  );
  const initialSuiteExecutionsData = useMemo(
    () =>
      buildLocalQueryResult(suiteExecutionsRows, {
        page: 0,
        pageSize: LANDING_TABLE_PAGE_SIZE,
        sorts: [],
        filters: [],
      }),
    [],
  );

  return (
    <Box component="section" sx={{ display: { xs: 'none', sm: 'block' } }}>
      <Box sx={{ maxWidth: width, mx: 'auto', width: '100%' }}>
        <Box sx={{ mb: s('md'), textAlign: 'center' }}>
          <Text role="sectionTitle" component="h2" transform="none" mb="xs">
            {landingCopy.executionTables.heading}
          </Text>
          <Text role="bodyMuted" sx={{ fontSize: LANDING_BODY_FONT_SIZE }}>
            {landingCopy.executionTables.intro}
          </Text>
        </Box>
        <Box
          sx={{
            width: '100%',
            minWidth: 0,
            border: `1px solid ${theme.app.colors.divider}`,
            borderRadius: r('card'),
            backgroundColor: theme.app.colors.surface,
            overflow: 'hidden',
            contain: 'layout paint',
            isolation: 'isolate',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: s('lg'),
              minHeight: `${CLONE_TABLE_PANEL_HEADER_HEIGHT}px`,
              height: `${CLONE_TABLE_PANEL_HEADER_HEIGHT}px`,
              px: s('lg'),
              pt: s('sm'),
              pb: s('sm'),
              borderBottom: `1px solid ${theme.app.colors.divider}`,
            }}
          >
            <Tabs
              value={tabValue}
              onChange={(_event, next) => {
                onSelect(next === 'suite-executions' ? 1 : 0);
              }}
              aria-label="Landing tables tabs"
            >
              <Tab
                label="Suites"
                value="suites"
                sx={{
                  fontSize: LANDING_TABLE_UI_FONT_SIZE,
                  lineHeight: (muiTheme) => muiTheme.typography.subtitle1.lineHeight,
                }}
              />
              <Tab
                label="Suite Executions"
                value="suite-executions"
                sx={{
                  fontSize: LANDING_TABLE_UI_FONT_SIZE,
                  lineHeight: (muiTheme) => muiTheme.typography.subtitle1.lineHeight,
                }}
              />
            </Tabs>
            <LandingToolbarPreview />
          </Box>
          <Box
            key={`landing-table-tab-${tabValue}`}
            sx={{
              width: '100%',
              minWidth: 0,
              animation: `landing-tab-fade-in 160ms ${theme.app.motion.easing.standard}`,
              '@keyframes landing-tab-fade-in': {
                from: { opacity: 0.35 },
                to: { opacity: 1 },
              },
            }}
          >
            {tabValue === 'suites' ? (
              <SuitesTable
                queryKey={gridQueryKey('landing/suites')}
                queryFn={suitesQuery}
                initialData={initialSuitesData}
                pageSizeOptions={[LANDING_TABLE_PAGE_SIZE]}
                initialPageSize={LANDING_TABLE_PAGE_SIZE}
                disablePageSizeSelect
                hideFooter
                disableColumnSorting
                disableColumnMenu
                persistKey={null}
                syncStateToUrl={false}
                showLastUpdated={false}
                showDeltas
                tokenOverride="local"
                disableLinks
                showToolbar={false}
                sparklineRangeDays={7}
                metricsStartDay="2026-03-03"
                metricsEndDay="2026-03-09"
                metricSeriesBySuiteIdOverride={landingSuiteMetricSeriesBySuiteId}
                tagColorByNameOverride={landingTagColorByName}
                columnVisibilityModel={{ actions: false }}
              />
            ) : (
              <SuiteExecutionsTable
                queryKey={gridQueryKey('landing/suite-executions')}
                queryFn={suiteExecutionsQuery}
                initialData={initialSuiteExecutionsData}
                pageSizeOptions={[LANDING_TABLE_PAGE_SIZE]}
                initialPageSize={LANDING_TABLE_PAGE_SIZE}
                disablePageSizeSelect
                hideFooter
                disableColumnSorting
                disableColumnMenu
                persistKey={null}
                syncStateToUrl={false}
                showLastUpdated={false}
                disableLinks
                showToolbar={false}
                tagColorByName={landingTagColorByName}
              />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
